import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ChevronLeft, Send, Loader2, CheckCircle, Mail, User, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const labels = {
    title: { fr: 'Contactez-nous', en: 'Contact us', nl: 'Contacteer ons' },
    subtitle: { fr: 'Une question, une suggestion ? Écrivez-nous !', en: 'Got a question or suggestion? Write to us!', nl: 'Een vraag of suggestie? Schrijf ons!' },
    name: { fr: 'Votre nom', en: 'Your name', nl: 'Uw naam' },
    namePlaceholder: { fr: 'Jean Dupont', en: 'John Smith', nl: 'Jan Janssen' },
    email: { fr: 'Votre email', en: 'Your email', nl: 'Uw e-mail' },
    subject: { fr: 'Sujet', en: 'Subject', nl: 'Onderwerp' },
    subjectPlaceholder: { fr: 'Ex: Question sur les réservations', en: 'E.g. Question about bookings', nl: 'Bijv. Vraag over boekingen' },
    message: { fr: 'Message', en: 'Message', nl: 'Bericht' },
    messagePlaceholder: { fr: 'Décrivez votre demande...', en: 'Describe your request...', nl: 'Beschrijf uw aanvraag...' },
    send: { fr: 'Envoyer le message', en: 'Send message', nl: 'Bericht versturen' },
    sending: { fr: 'Envoi en cours...', en: 'Sending...', nl: 'Verzenden...' },
    successTitle: { fr: 'Message envoyé !', en: 'Message sent!', nl: 'Bericht verzonden!' },
    successDesc: { fr: 'Nous reviendrons vers vous dans les plus brefs délais.', en: 'We\'ll get back to you as soon as possible.', nl: 'We nemen zo snel mogelijk contact met u op.' },
    sendAnother: { fr: 'Envoyer un autre message', en: 'Send another message', nl: 'Nog een bericht sturen' },
    backHome: { fr: 'Retour à l\'accueil', en: 'Back to home', nl: 'Terug naar home' },
    toastSuccess: { fr: 'Message envoyé avec succès !', en: 'Message sent successfully!', nl: 'Bericht succesvol verzonden!' },
    toastError: { fr: 'Erreur lors de l\'envoi. Réessayez.', en: 'Error sending. Please try again.', nl: 'Fout bij verzenden. Probeer opnieuw.' },
  };

  const l = (key) => labels[key]?.[lang] || labels[key]?.['fr'] || key;

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(l('toastSuccess'));
        setSent(true);
      } else {
        toast.error(l('toastError'));
      }
    } catch (e) {
      toast.error(l('toastError'));
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center max-w-md animate-fade-in">
          <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold mb-3">{l('successTitle')}</h2>
          <p className="text-muted-foreground mb-8">{l('successDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }}>
              {l('sendAnother')}
            </Button>
            <Link to="/"><Button className="btn-primary">{l('backHome')}</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <Link to="/"><img src="/logo.png" alt="Motivya" className="h-8 w-auto" /></Link>
            <div className="w-6" />
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold uppercase mb-2" data-testid="contact-title">{l('title')}</h1>
          <p className="text-muted-foreground">{l('subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-6 sm:p-8 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name">{l('name')} *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="name" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder={l('namePlaceholder')} className="pl-10" required data-testid="contact-name" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{l('email')} *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input id="email" type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="email@exemple.com" className="pl-10" required data-testid="contact-email" />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">{l('subject')} *</Label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input id="subject" value={form.subject} onChange={(e) => setForm(p => ({ ...p, subject: e.target.value }))}
                placeholder={l('subjectPlaceholder')} className="pl-10" required data-testid="contact-subject" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">{l('message')} *</Label>
            <Textarea id="message" value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))}
              placeholder={l('messagePlaceholder')} rows={5} required data-testid="contact-message" />
          </div>

          <Button type="submit" className="btn-primary w-full h-12 text-lg" disabled={sending} data-testid="contact-submit">
            {sending ? (
              <><Loader2 className="w-5 h-5 animate-spin mr-2" />{l('sending')}</>
            ) : (
              <><Send className="w-5 h-5 mr-2" />{l('send')}</>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
